import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { Rnd } from "react-rnd";

import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BrushIcon from "@mui/icons-material/Brush";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ChairIcon from "@mui/icons-material/Chair";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailIcon from "@mui/icons-material/Email";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PaletteIcon from "@mui/icons-material/Palette";
import PaymentIcon from "@mui/icons-material/Payment";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import ShareIcon from "@mui/icons-material/Share";
import StraightenIcon from "@mui/icons-material/Straighten";
import TuneIcon from "@mui/icons-material/Tune";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import WallpaperIcon from "@mui/icons-material/Wallpaper";

const RAW_API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const SITE_NAME = "Art For My Room";
const CONTACT_EMAIL = "info@artformyroom.com";

const ASSETS = {
  logo: "/logo.png",
  findVideo: "/findpainting.mp4",
};

const TURQUOISE = "#40E0D0";
const TURQUOISE_DARK = "#00796B";
const TURQUOISE_DEEP = "#005E58";
const TURQUOISE_MIST = "#DDFBF8";
const TURQUOISE_PALE = "#F4FFFD";

const GOLD = "#D4AF37";
const GOLD_DARK = "#8A6500";
const GOLD_MIST = "#FFF4CF";

const BURGUNDY = "#7A1232";
const BURGUNDY_DARK = "#4A071D";
const BURGUNDY_MIST = "#FBEAF0";

const INK = "#111827";
const NAVY = "#111A2E";
const CANVAS = "#F7F4EF";
const PAPER = "#FDFCF9";
const WHITE = "#FFFFFF";
const MUTED = "#6B7280";
const SOFT_MUTED = "#8A8A9A";
const LINE = "rgba(17, 24, 39, 0.11)";

const HEADING_FONT = "'Space Grotesk', 'Inter', system-ui, sans-serif";
const BODY_FONT = "'Inter', 'Segoe UI', system-ui, sans-serif";
const MONO_FONT = "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace";

const COUNTRY_OPTIONS = [
  { value: "GB", label: "United Kingdom" },
  { value: "US", label: "United States" },
  { value: "AL", label: "Albania" },
  { value: "IT", label: "Italy" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "ES", label: "Spain" },
  { value: "NL", label: "Netherlands" },
  { value: "BE", label: "Belgium" },
  { value: "IE", label: "Ireland" },
  { value: "GR", label: "Greece" },
  { value: "PT", label: "Portugal" },
  { value: "CH", label: "Switzerland" },
  { value: "AT", label: "Austria" },
  { value: "SE", label: "Sweden" },
  { value: "NO", label: "Norway" },
  { value: "DK", label: "Denmark" },
  { value: "FI", label: "Finland" },
  { value: "PL", label: "Poland" },
  { value: "RO", label: "Romania" },
  { value: "BG", label: "Bulgaria" },
  { value: "HR", label: "Croatia" },
  { value: "RS", label: "Serbia" },
  { value: "MK", label: "North Macedonia" },
  { value: "XK", label: "Kosovo" },
  { value: "ME", label: "Montenegro" },
  { value: "BA", label: "Bosnia and Herzegovina" },
  { value: "TR", label: "Turkey" },
  { value: "CY", label: "Cyprus" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
  { value: "IN", label: "India" },
  { value: "CN", label: "China" },
  { value: "JP", label: "Japan" },
  { value: "KR", label: "South Korea" },
  { value: "BR", label: "Brazil" },
  { value: "MX", label: "Mexico" },
  { value: "ZA", label: "South Africa" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "OTHER", label: "Other" },
];

const CATEGORY_ORDER = ["painting", "print", "sculpture", "decorative_object"];

const ARTWORK_CATEGORIES = [
  {
    value: "painting",
    label: "Paintings",
    shortLabel: "Painting",
    resultTitle: "Original paintings",
    resultSubtitle: "Artist originals for walls that need presence.",
    emptyText: "No painting matches found yet.",
    negotiable: true,
    icon: <BrushIcon />,
  },
  {
    value: "print",
    label: "Prints",
    shortLabel: "Print",
    resultTitle: "A3 prints",
    resultSubtitle: "Affordable fixed-price prints for quick room styling.",
    emptyText: "No print matches found yet.",
    negotiable: false,
    icon: <PaletteIcon />,
  },
  {
    value: "sculpture",
    label: "Sculptures",
    shortLabel: "Sculpture",
    resultTitle: "Sculptures",
    resultSubtitle: "Artist-made pieces for corners, consoles and shelves.",
    emptyText: "No sculpture matches found yet.",
    negotiable: true,
    icon: <ViewInArIcon />,
  },
  {
    value: "decorative_object",
    label: "Decorative Objects",
    shortLabel: "Object",
    resultTitle: "Decorative objects",
    resultSubtitle: "Small finishing pieces for texture and character.",
    emptyText: "No decorative object matches found yet.",
    negotiable: false,
    icon: <ChairIcon />,
  },
];

const CATEGORY_LOOKUP = ARTWORK_CATEGORIES.reduce((acc, item) => {
  acc[item.value] = item;
  return acc;
}, {});

const EMPTY_GROUPED_RESULTS = {
  painting: [],
  print: [],
  sculpture: [],
  decorative_object: [],
};

function buildApiUrl(path) {
  const base = RAW_API_BASE_URL.replace(/\/+$/, "");
  const cleanPath = path.replace(/^\/+/, "");

  if (!base) {
    return `/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
  }

  if (base.endsWith("/api")) {
    return `${base}/${cleanPath.replace(/^api\//, "")}`;
  }

  return `${base}/${cleanPath.startsWith("api/") ? cleanPath : `api/${cleanPath}`}`;
}

function forceHttpsUrl(url) {
  if (!url) return "";
  const cleanUrl = String(url).trim();

  if (cleanUrl.startsWith("http://")) {
    return `https://${cleanUrl.slice("http://".length)}`;
  }

  return cleanUrl;
}

function getCountryLabel(code) {
  const match = COUNTRY_OPTIONS.find((country) => country.value === code);
  return match ? match.label : code || "";
}

function safeArray(value) {
  if (Array.isArray(value)) return value;

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return [];
}

function firstValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return undefined;
}

function toBoolean(value, fallback = true) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  if (typeof value === "string") {
    const clean = value.trim().toLowerCase();

    if (["false", "0", "no", "none", "null", "undefined", "closed"].includes(clean)) {
      return false;
    }

    if (["true", "1", "yes", "open"].includes(clean)) {
      return true;
    }
  }

  return Boolean(value);
}

function cleanMoneyNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") return fallback;

  const number = Number(String(value).replace("£", "").replace(",", "").trim());

  if (!Number.isFinite(number)) return fallback;

  return number;
}

function moneyLabel(value, fallback = "Price TBC") {
  const number = cleanMoneyNumber(value, null);

  if (number === null) return fallback;

  return `£${number.toFixed(2)}`;
}

function normaliseCategory(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");

  const aliases = {
    actual: "painting",
    actual_painting: "painting",
    actual_paintings: "painting",
    original_painting: "painting",
    original_paintings: "painting",
    paintings: "painting",
    print_art: "print",
    art_print: "print",
    art_prints: "print",
    prints: "print",
    sculptures: "sculpture",
    decorative: "decorative_object",
    decorative_objects: "decorative_object",
    object: "decorative_object",
    objects: "decorative_object",
  };

  const category = aliases[raw] || raw || "painting";

  if (CATEGORY_ORDER.includes(category)) return category;

  return "painting";
}

function normaliseSource(value, category = "painting") {
  const raw = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_");

  const aliases = {
    actual: "actual_painting",
    actualpainting: "actual_painting",
    actual_painting: "actual_painting",
    actual_paintings: "actual_painting",
    painting: "actual_painting",
    paintings: "actual_painting",
    original_painting: "actual_painting",
    original_paintings: "actual_painting",
    print: "print",
    prints: "print",
    sculpture: "sculpture",
    sculptures: "sculpture",
    decorative: "decorative_object",
    decorative_objects: "decorative_object",
    object: "decorative_object",
    objects: "decorative_object",
  };

  if (raw) return aliases[raw] || raw;

  const cleanCategory = normaliseCategory(category);

  if (cleanCategory === "print") return "print";
  if (cleanCategory === "sculpture") return "sculpture";
  if (cleanCategory === "decorative_object") return "decorative_object";

  return "actual_painting";
}

function getCategoryLabel(value) {
  const category = normaliseCategory(value);
  return CATEGORY_LOOKUP[category]?.shortLabel || "Artwork";
}

function isNegotiableCategory(value) {
  const category = normaliseCategory(value);
  return category === "painting" || category === "sculpture";
}

function isBuyOnlyCategory(value) {
  const category = normaliseCategory(value);
  return category === "print" || category === "decorative_object";
}

function normaliseImageUrl(value) {
  if (!value) return "";

  if (typeof value === "string") return forceHttpsUrl(value);

  if (typeof value === "object") {
    return forceHttpsUrl(
      value.secure_url ||
        value.url ||
        value.image_url ||
        ""
    );
  }

  return "";
}

function buildSizeLabel(record, category) {
  const cleanCategory = normaliseCategory(category);

  if (cleanCategory === "print") return "A3 size";
  if (record?.size_label) return record.size_label;

  const width = record?.width_cm;
  const height = record?.height_cm;
  const depth = record?.depth_cm;
  const diameter = record?.diameter_cm;

  if (width && height && depth) return `${width} × ${height} × ${depth} cm`;
  if (width && height) return `${width} × ${height} cm`;
  if (diameter) return `Diameter ${diameter} cm`;
  if (width) return `Width ${width} cm`;
  if (height) return `Height ${height} cm`;

  return "Size TBC";
}

function normaliseMatchScore(score) {
  if (score === null || score === undefined || score === "") return 0;

  const number = Number(score);

  if (!Number.isFinite(number)) return 0;

  if (number <= 1) {
    return Math.round(Math.max(0, Math.min(1, number)) * 100);
  }

  return Math.round(Math.max(0, Math.min(100, number)));
}

function getArtistLabel(record) {
  return (
    record?.buyer_facing_artist_name ||
    record?.public_artist_label ||
    record?.artist_name ||
    record?.artist ||
    "Artist"
  );
}

function getActualPriceValue(record) {
  return cleanMoneyNumber(
    firstValue(
      record?.price_gbp,
      record?.price,
      record?.buy_now_price_gbp,
      record?.current_offer_minimum_gbp,
      record?.starting_offer_minimum_gbp
    ),
    0
  );
}

function getOfferMinimumValue(record) {
  const minimum = cleanMoneyNumber(
    firstValue(
      record?.current_offer_minimum_gbp,
      record?.starting_offer_minimum_gbp,
      record?.price_gbp,
      record?.price,
      record?.buy_now_price_gbp
    ),
    0
  );

  if (minimum > 0) return minimum;

  return 55;
}

function getPriceLabel(record) {
  return (
    record?.actual_price_label ||
    record?.price_label ||
    moneyLabel(getActualPriceValue(record), "Price TBC")
  );
}

function getOfferFloorLabel(record) {
  const category = normaliseCategory(record?.artwork_category);

  if (category === "print") return "A3 size · Buy Now only";
  if (category === "decorative_object") return "Buy Now only";

  return (
    record?.minimum_offer_label ||
    `Offers from £${getOfferMinimumValue(record).toFixed(2)}`
  );
}

function getCommerceNote(record) {
  const category = normaliseCategory(record?.artwork_category);

  if (category === "painting") return "Original painting · Make Room Offer";
  if (category === "sculpture") return "Artist sculpture · Make Room Offer";
  if (category === "print") return "A3 print · fixed price";
  if (category === "decorative_object") return "Decorative object · fixed price";

  return "Art For My Room";
}

function getArtworkKey(record) {
  if (!record) return "";

  const source = normaliseSource(
    record.artwork_source || record.source_type || record.source,
    record.artwork_category
  );

  return `${source}-${record.id}`;
}

function slugifyArtworkText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function getArtworkSeoPath(record) {
  if (!record?.id) return "#";

  const category = normaliseCategory(record.artwork_category || record.category || record.type);
  const artistOrCategory =
    category === "painting" || category === "sculpture"
      ? getArtistLabel(record)
      : getCategoryLabel(category);

  const slug =
    record.slug ||
    record.seo_slug ||
    slugifyArtworkText(`${record.title || "artwork"} ${artistOrCategory}`) ||
    `artwork-${record.id}`;

  return `/artwork/${category}/${slug}/${encodeURIComponent(record.id)}`;
}

function getArtworkSeoAnchorText(record) {
  const categoryLabel = getCategoryLabel(record?.artwork_category);
  const title = record?.title || "Artwork";
  const artist = getArtistLabel(record);

  if (artist && artist !== "Artist") {
    return `${title} ${categoryLabel} by ${artist}`;
  }

  return `${title} ${categoryLabel}`;
}

function getArtworkCountryDisplay(record) {
  return (
    record?.country_display ||
    getCountryLabel(record?.country) ||
    record?.country ||
    "TBC"
  );
}

function getArtworkMediumDisplay(record) {
  const category = normaliseCategory(record?.artwork_category);

  return (
    record?.medium ||
    record?.material ||
    (category === "print" ? "A3 art print" : getCategoryLabel(category)) ||
    "TBC"
  );
}

function getArtworkDescription(record) {
  if (!record) return "";

  return (
    record.best_public_description ||
    record.generated_description ||
    record.description ||
    record.artwork_description ||
    ""
  );
}

function canBuyNow(record) {
  if (!record) return false;
  if (toBoolean(record.can_buy_now, true) === false) return false;
  if (toBoolean(record.buy_now_enabled, true) === false) return false;
  if (toBoolean(record.for_sale, true) === false) return false;

  return getActualPriceValue(record) > 0;
}

function canSendRoomOffer(record) {
  if (!record) return false;

  const category = normaliseCategory(record.artwork_category || record.category || record.type);
  const source = normaliseSource(
    record.artwork_source ||
      record.source_type ||
      record.source ||
      record.source_table,
    category
  );

  if (!isNegotiableCategory(category)) return false;
  if (isBuyOnlyCategory(category)) return false;

  const validNegotiationSource =
    source === "actual_painting" ||
    source === "sculpture" ||
    source === "actual";

  if (!validNegotiationSource) return false;

  if (toBoolean(record.for_sale, true) === false) return false;
  if (toBoolean(record.room_offer_enabled, true) === false) return false;
  if (toBoolean(record.accepts_room_offers_now, true) === false) return false;
  if (toBoolean(record.can_negotiate, true) === false) return false;

  const offerStatus = record.offer_status || "open";

  return offerStatus === "open";
}

function getAspectRatio(record) {
  const fromHint = Number(record?.overlay_hint?.aspect_ratio);
  const fromRecommended = Number(record?.recommended_overlay_ratio);
  const fromPhysical = Number(record?.physical_aspect_ratio);
  const width = Number(record?.width_cm);
  const height = Number(record?.height_cm);

  if (Number.isFinite(fromHint) && fromHint > 0) return fromHint;
  if (Number.isFinite(fromRecommended) && fromRecommended > 0) return fromRecommended;
  if (Number.isFinite(fromPhysical) && fromPhysical > 0) return fromPhysical;

  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return width / height;
  }

  const category = normaliseCategory(record?.artwork_category);

  if (category === "print") return 29.7 / 42;
  if (category === "sculpture") return 0.72;
  if (category === "decorative_object") return 0.9;

  return 1;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function getDefaultPlacement(record, stage, existingCount = 0) {
  const stageWidth = stage.width || 760;
  const stageHeight = stage.height || 520;
  const hint = record?.overlay_hint || {};
  const aspectRatio = getAspectRatio(record);
  const category = normaliseCategory(record?.artwork_category);

  let widthPercent = Number(hint.initial_width_percent);
  let xPercent = Number(hint.x_percent);
  let yPercent = Number(hint.y_percent);

  if (!Number.isFinite(widthPercent) || widthPercent <= 0) {
    if (category === "sculpture") widthPercent = 16;
    else if (category === "decorative_object") widthPercent = 13;
    else if (category === "print") widthPercent = 19;
    else widthPercent = 24;
  }

  if (!Number.isFinite(xPercent)) xPercent = 34 + existingCount * 6;

  if (!Number.isFinite(yPercent)) {
    if (category === "sculpture") yPercent = 58;
    else if (category === "decorative_object") yPercent = 62;
    else yPercent = 28;
  }

  let width = stageWidth * (widthPercent / 100);
  let height = width / aspectRatio;

  const minSize = 54;
  const maxWidth = stageWidth * 0.64;
  const maxHeight = stageHeight * 0.78;

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }

  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspectRatio;
  }

  width = clamp(width, minSize, maxWidth);
  height = clamp(height, minSize, maxHeight);

  const x = clamp(stageWidth * (xPercent / 100), 8, Math.max(8, stageWidth - width - 8));
  const y = clamp(stageHeight * (yPercent / 100), 8, Math.max(8, stageHeight - height - 8));

  return {
    size: {
      width: Math.round(width),
      height: Math.round(height),
    },
    position: {
      x: Math.round(x),
      y: Math.round(y),
    },
  };
}

function normaliseArtwork(record, index, fallbackCategory = "painting") {
  const overlayHint =
    record?.overlay_hint && typeof record.overlay_hint === "object"
      ? record.overlay_hint
      : {};

  const category = normaliseCategory(
    firstValue(
      record?.artwork_category,
      record?.category,
      record?.type,
      fallbackCategory
    )
  );

  const source = normaliseSource(
    firstValue(
      record?.artwork_source,
      record?.source_type,
      record?.source,
      record?.source_table
    ),
    category
  );

  const imageUrl = normaliseImageUrl(
    firstValue(
      record?.image_url,
      record?.transparent_cutout_image_url,
      record?.room_mockup_image_url,
      record?.secure_url,
      record?.image,
      record?.artwork_image,
      record?.painting_image,
      record?.thumbnail_url,
      record?.cloudinary_url,
      record?.file_url
    )
  );

  const bestDescription =
    record?.best_public_description ||
    record?.generated_description ||
    record?.description ||
    record?.artwork_description ||
    "";

  const matchScore = normaliseMatchScore(
    firstValue(
      record?.match_score,
      record?.match_score_percent,
      record?.similarity_score,
      record?.score
    )
  );

  const roomOfferEnabled = isNegotiableCategory(category)
    ? toBoolean(record?.room_offer_enabled, true)
    : false;

  const acceptsRoomOffersNow = isNegotiableCategory(category)
    ? toBoolean(record?.accepts_room_offers_now, true)
    : false;

  const buyNowEnabled = toBoolean(record?.buy_now_enabled, true);
  const forSale = toBoolean(record?.for_sale, true);
  const uniqueId = record?.id || `${source}-${category}-${index}`;

  return {
    ...record,
    id: uniqueId,
    source_type: source,
    artwork_source: source,
    artwork_category: category,
    artwork_category_label: record?.artwork_category_label || getCategoryLabel(category),
    title: record?.title || record?.name || record?.painting_title || "Untitled artwork",
    artist_name: getArtistLabel(record),
    buyer_facing_artist_name: getArtistLabel(record),
    image_url: imageUrl,
    transparent_cutout_image_url: forceHttpsUrl(record?.transparent_cutout_image_url || ""),
    description: bestDescription,
    best_public_description: bestDescription,
    medium:
      record?.medium ||
      record?.material ||
      (category === "print" ? "A3 art print" : getCategoryLabel(category)),
    size_label: buildSizeLabel(record, category),
    country: record?.country || "",
    country_display: record?.country_display || getCountryLabel(record?.country),
    colour_palette: safeArray(record?.colour_palette || record?.color_palette || record?.palette),
    visual_features: record?.visual_features || {},
    overlay_hint: overlayHint,
    physical_aspect_ratio:
      record?.physical_aspect_ratio ||
      record?.recommended_overlay_ratio ||
      overlayHint.aspect_ratio ||
      null,
    recommended_overlay_ratio:
      record?.recommended_overlay_ratio ||
      overlayHint.aspect_ratio ||
      record?.physical_aspect_ratio ||
      null,
    match_score: matchScore,
    match_reason: record?.match_reason || "",
    price_gbp: firstValue(record?.price_gbp, record?.price, record?.buy_now_price_gbp, ""),
    buy_now_price_gbp: firstValue(record?.buy_now_price_gbp, record?.price_gbp, record?.price, ""),
    actual_price_label: record?.actual_price_label || record?.price_label || "",
    current_offer_minimum_gbp: isNegotiableCategory(category)
      ? firstValue(
          record?.current_offer_minimum_gbp,
          record?.starting_offer_minimum_gbp,
          record?.price_gbp,
          record?.buy_now_price_gbp,
          "55.00"
        )
      : "",
    minimum_offer_label: isNegotiableCategory(category)
      ? record?.minimum_offer_label || `Offers from £${getOfferMinimumValue(record).toFixed(2)}`
      : "Buy Now only",
    estimated_postage_fee_gbp: record?.estimated_postage_fee_gbp || "",
    estimated_postage_fee_label: record?.estimated_postage_fee_label || "",
    estimated_postage_region: record?.estimated_postage_region || "",
    estimated_postage_size_band: record?.estimated_postage_size_band || "",
    offer_status: isNegotiableCategory(category) ? record?.offer_status || "open" : "not_accepting",
    room_offer_enabled: roomOfferEnabled,
    accepts_room_offers_now: acceptsRoomOffersNow,
    can_negotiate: isNegotiableCategory(category) && roomOfferEnabled && acceptsRoomOffersNow,
    can_buy_now: toBoolean(record?.can_buy_now, true),
    buy_now_enabled: buyNowEnabled,
    for_sale: forSale,
  };
}

function createEmptyGroupedResults() {
  return {
    painting: [],
    print: [],
    sculpture: [],
    decorative_object: [],
  };
}

function extractArrayFromPossibleKeys(source, keys) {
  if (!source || typeof source !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key];
  }

  return [];
}

function extractGroupedResults(data) {
  const grouped = createEmptyGroupedResults();

  const backendGrouped =
    data?.matches_by_category ||
    data?.results_by_category ||
    data?.artworks_by_category ||
    data?.grouped_matches ||
    null;

  if (backendGrouped && typeof backendGrouped === "object") {
    grouped.painting = extractArrayFromPossibleKeys(backendGrouped, [
      "painting",
      "paintings",
      "actual_paintings",
      "actual_painting",
    ]);

    grouped.print = extractArrayFromPossibleKeys(backendGrouped, [
      "print",
      "prints",
      "a3_prints",
    ]);

    grouped.sculpture = extractArrayFromPossibleKeys(backendGrouped, [
      "sculpture",
      "sculptures",
    ]);

    grouped.decorative_object = extractArrayFromPossibleKeys(backendGrouped, [
      "decorative_object",
      "decorative_objects",
      "objects",
    ]);

    return grouped;
  }

  const flatMatches = Array.isArray(data?.matches) ? data.matches : [];

  flatMatches.forEach((record) => {
    const category = normaliseCategory(
      firstValue(record?.artwork_category, record?.category, record?.type)
    );

    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(record);
  });

  return grouped;
}

function resizeHandleStyle(position = "absolute") {
  return {
    width: 16,
    height: 16,
    borderRadius: 999,
    background: GOLD,
    border: `2px solid ${WHITE}`,
    boxShadow: "0 6px 16px rgba(0,0,0,0.22)",
    position,
  };
}

const PageShell = styled(Box)({
  minHeight: "100vh",
  color: INK,
  fontFamily: BODY_FONT,
  background:
    `radial-gradient(circle at 4% 4%, ${alpha(TURQUOISE, 0.22)}, transparent 28%),
     radial-gradient(circle at 95% 8%, ${alpha(GOLD, 0.20)}, transparent 28%),
     radial-gradient(circle at 95% 65%, ${alpha(BURGUNDY, 0.12)}, transparent 30%),
     linear-gradient(180deg, #ffffff 0%, #f7fffd 44%, #fff8ee 100%)`,
});

const AppHeader = styled(Paper)({
  borderRadius: 0,
  background: INK,
  color: WHITE,
  borderBottom: `1px solid ${alpha(WHITE, 0.08)}`,
  boxShadow: "0 2px 18px rgba(0,0,0,0.18)",
});

const AppPanel = styled(Paper)({
  borderRadius: 22,
  border: `1px solid ${LINE}`,
  background: alpha(PAPER, 0.94),
  boxShadow: "0 16px 50px rgba(17, 24, 39, 0.07)",
  overflow: "hidden",
});

const CanvasPanel = styled(Paper)({
  borderRadius: 26,
  background:
    `linear-gradient(180deg, ${alpha(WHITE, 0.96)} 0%, ${alpha(CANVAS, 0.96)} 100%)`,
  border: `1px solid ${LINE}`,
  boxShadow: "0 24px 70px rgba(17, 24, 39, 0.10)",
  overflow: "hidden",
});

const PrimaryButton = styled(Button)({
  textTransform: "none",
  fontWeight: 850,
  borderRadius: 10,
  padding: "12px 20px",
  background: TURQUOISE,
  color: INK,
  boxShadow: `0 12px 28px ${alpha(TURQUOISE, 0.25)}`,
  "&:hover": {
    background: TURQUOISE_DARK,
    color: WHITE,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: alpha(TURQUOISE, 0.32),
    color: alpha(INK, 0.55),
  },
});

const BurgundyButton = styled(Button)({
  textTransform: "none",
  fontWeight: 850,
  borderRadius: 10,
  padding: "12px 20px",
  background: BURGUNDY,
  color: WHITE,
  boxShadow: `0 12px 28px ${alpha(BURGUNDY, 0.20)}`,
  "&:hover": {
    background: BURGUNDY_DARK,
    transform: "translateY(-1px)",
  },
  "&.Mui-disabled": {
    background: alpha(BURGUNDY, 0.32),
    color: alpha(WHITE, 0.72),
  },
});

const SecondaryButton = styled(Button)({
  textTransform: "none",
  fontWeight: 820,
  borderRadius: 10,
  padding: "11px 18px",
  color: INK,
  border: `1.5px solid ${LINE}`,
  background: WHITE,
  "&:hover": {
    background: TURQUOISE_PALE,
    borderColor: alpha(TURQUOISE, 0.42),
  },
});

const GhostButton = styled(Button)({
  textTransform: "none",
  fontWeight: 820,
  borderRadius: 10,
  padding: "10px 16px",
  color: SOFT_MUTED,
  "&:hover": {
    color: TURQUOISE_DARK,
    background: alpha(TURQUOISE, 0.08),
  },
});

const MinimalInput = styled(TextField)({
  "& label": {
    color: alpha(INK, 0.58),
  },
  "& label.Mui-focused": {
    color: TURQUOISE_DARK,
  },
  "& .MuiSelect-icon": {
    color: TURQUOISE_DARK,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    background: WHITE,
    "& fieldset": {
      borderColor: LINE,
    },
    "&:hover fieldset": {
      borderColor: alpha(TURQUOISE, 0.45),
    },
    "&.Mui-focused fieldset": {
      borderColor: TURQUOISE,
      boxShadow: `0 0 0 3px ${alpha(TURQUOISE, 0.14)}`,
    },
  },
});

const UploadZone = styled(Box)({
  borderRadius: 18,
  border: `2px dashed ${alpha(TURQUOISE, 0.62)}`,
  background:
    `linear-gradient(180deg, ${alpha(TURQUOISE_MIST, 0.80)} 0%, ${WHITE} 100%)`,
  padding: 22,
  cursor: "pointer",
  textAlign: "center",
  transition: "0.18s ease",
  "&:hover": {
    borderColor: GOLD,
    transform: "translateY(-2px)",
    boxShadow: `0 14px 34px ${alpha(TURQUOISE, 0.12)}`,
  },
});

const SmallMeta = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: MUTED, fontWeight: 850, textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 900, color: INK, fontSize: "0.94rem", lineHeight: 1.25 }}>
      {value || "TBC"}
    </Typography>
  </Box>
);

const StatBox = ({ value, label }) => (
  <Box
    sx={{
      bgcolor: alpha(WHITE, 0.08),
      border: `1px solid ${alpha(WHITE, 0.12)}`,
      borderRadius: 3,
      p: 1.4,
      minWidth: 118,
    }}
  >
    <Typography sx={{ fontWeight: 950, fontSize: "1.15rem", lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ color: alpha(WHITE, 0.62), fontSize: 12, mt: 0.45 }}>
      {label}
    </Typography>
  </Box>
);

const CategoryChip = ({ category, selected, onClick }) => (
  <Chip
    clickable
    onClick={onClick}
    icon={category.icon}
    label={category.label}
    sx={{
      px: 0.7,
      height: 38,
      borderRadius: 999,
      fontWeight: 850,
      bgcolor: selected ? INK : WHITE,
      color: selected ? WHITE : INK,
      border: `1px solid ${selected ? INK : LINE}`,
      "& .MuiChip-icon": {
        color: selected ? TURQUOISE : TURQUOISE_DARK,
      },
      "&:hover": {
        bgcolor: selected ? NAVY : TURQUOISE_MIST,
      },
    }}
  />
);

const PaintStreak = ({ sx }) => (
  <Box
    sx={{
      position: "absolute",
      width: "130%",
      height: 70,
      left: "-15%",
      top: "50%",
      transform: "rotate(-7deg)",
      background:
        `linear-gradient(90deg, ${alpha(TURQUOISE, 0.14)}, ${alpha(GOLD, 0.20)}, ${alpha(BURGUNDY, 0.16)})`,
      filter: "blur(8px)",
      borderRadius: "999px",
      pointerEvents: "none",
      ...sx,
    }}
  />
);

function getGrowthTracker() {
  if (typeof window === "undefined") return null;

  return window.ArtForMyRoomGrowth || null;
}

async function recordGrowthEvent(eventType, metadata = {}) {
  const tracker = getGrowthTracker();

  if (!tracker?.recordEvent) {
    return null;
  }

  try {
    return await tracker.recordEvent(eventType, metadata);
  } catch (error) {
    console.warn(`Could not record growth event: ${eventType}`, error);
    return null;
  }
}

async function createGrowthShareResult(artwork) {
  const tracker = getGrowthTracker();

  if (!tracker?.createSharedResult) {
    throw new Error(
      "Sharing is still loading. Please refresh the page and try again."
    );
  }

  const numericArtworkId = Number(artwork?.id);
  const paintingId =
    Number.isInteger(numericArtworkId) && numericArtworkId > 0
      ? numericArtworkId
      : null;

  const paintingTitle =
    artwork?.title ||
    artwork?.painting_title ||
    artwork?.name ||
    "Selected artwork";

  const artistName =
    artwork?.buyer_facing_artist_name ||
    artwork?.artist_name ||
    artwork?.artist ||
    "";

  const matchPercentage = normaliseMatchScore(
    firstValue(
      artwork?.match_score,
      artwork?.match_score_percent,
      artwork?.similarity_score,
      artwork?.similarity,
      artwork?.score
    )
  );

  return tracker.createSharedResult({
    paintingId,
    paintingTitle,
    artistName,
    matchPercentage,
    resultHeadline: `This room found its artistic soulmate: ${paintingTitle}`,
    resultDescription:
      `${paintingTitle} was selected because its colours, atmosphere and ` +
      "visual character complement this room.",
    cardImageUrl: "",
    allowPublicGallery: false,
  });
}

function ActualRoomMatcherPage() {
  const fileInputRef = useRef(null);
  const stageRef = useRef(null);
  const roomPreviewSectionRef = useRef(null);

  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [country, setCountry] = useState("GB");

  const [selectedCategories, setSelectedCategories] = useState([...CATEGORY_ORDER]);
  const [roomFile, setRoomFile] = useState(null);
  const [roomPreviewUrl, setRoomPreviewUrl] = useState("");
  const [roomUploadId, setRoomUploadId] = useState(null);

  const [stageSize, setStageSize] = useState({ width: 900, height: 560 });
  const [matchesByCategory, setMatchesByCategory] = useState(EMPTY_GROUPED_RESULTS);
  const [placedItems, setPlacedItems] = useState([]);
  const [activePlacementId, setActivePlacementId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [buyingId, setBuyingId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [sharingArtworkKey, setSharingArtworkKey] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareError, setShareError] = useState("");

  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerArtwork, setOfferArtwork] = useState(null);
  const [offerForm, setOfferForm] = useState({
    buyer_name: "",
    buyer_email: "",
    amount_gbp: "",
    message: "",
  });
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [offerError, setOfferError] = useState("");

  const [offerSentDialogOpen, setOfferSentDialogOpen] = useState(false);
  const [offerSentArtwork, setOfferSentArtwork] = useState(null);
  const [offerSentSummary, setOfferSentSummary] = useState("");
  const [sentOfferArtworkKeys, setSentOfferArtworkKeys] = useState(() => new Set());

  const matchUrl = useMemo(() => buildApiUrl("artworks/match-room/"), []);
  const fallbackMatchUrl = useMemo(() => buildApiUrl("actual-paintings/match-room/"), []);
  const offerUrl = useMemo(() => buildApiUrl("room-offers/create/"), []);
  const buyNowUrl = useMemo(() => buildApiUrl("buy-now/create/"), []);

  const matches = useMemo(() => {
    return CATEGORY_ORDER.flatMap((category) => matchesByCategory[category] || []);
  }, [matchesByCategory]);

  const totalMatches = matches.length;

  const selectedCategoryObjects = useMemo(() => {
    return CATEGORY_ORDER
      .filter((category) => selectedCategories.includes(category))
      .map((category) => CATEGORY_LOOKUP[category]);
  }, [selectedCategories]);

  const activePlacedItem = placedItems.find((item) => item.placementId === activePlacementId);

  const setupProgress = useMemo(() => {
    const checks = [
      buyerEmail.trim(),
      country,
      roomFile,
      selectedCategories.length,
    ];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [buyerEmail, country, roomFile, selectedCategories.length]);

  useEffect(() => {
    document.title = "Find Art For My Room | Paintings, Prints, Sculptures and Decor";

    const description =
      "Upload your room and match it with paintings, A3 prints, sculptures and decorative objects.";

    let meta = document.querySelector('meta[name="description"]');

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", description);
  }, []);

  useEffect(() => {
    const tracker = getGrowthTracker();

    if (!tracker?.getReferralStatus) {
      return;
    }

    tracker.getReferralStatus().catch((trackingError) => {
      console.warn("Could not initialise referral tracking.", trackingError);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (roomPreviewUrl) {
        URL.revokeObjectURL(roomPreviewUrl);
      }
    };
  }, [roomPreviewUrl]);

  useEffect(() => {
    const element = stageRef.current;

    if (!element) return undefined;

    const update = () => {
      const rect = element.getBoundingClientRect();

      if (rect.width > 0 && rect.height > 0) {
        setStageSize({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    update();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);

      return () => {
        window.removeEventListener("resize", update);
      };
    }

    const observer = new ResizeObserver(update);
    observer.observe(element);
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [roomPreviewUrl]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateBeforeMatch = () => {
    if (!buyerEmail.trim()) return "Please enter your email.";
    if (!validateEmail(buyerEmail.trim())) return "Please enter a valid email.";
    if (!country) return "Please choose your country.";
    if (!roomFile) return "Please upload a room image.";
    if (!selectedCategories.length) return "Please choose at least one art category.";
    return "";
  };

  const scrollToRoomPreview = () => {
    window.setTimeout(() => {
      roomPreviewSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);
  };

  const handleRoomFileChange = (event) => {
    const file = event.target.files?.[0];

    setError("");
    setSuccess("");
    setShareMessage("");
    setShareError("");
    setMatchesByCategory(createEmptyGroupedResults());
    setPlacedItems([]);
    setRoomUploadId(null);

    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      setError("Please upload a JPG, PNG or WEBP image.");
      return;
    }

    if (file.size > 12 * 1024 * 1024) {
      setError("The image is too large. Maximum size is 12MB.");
      return;
    }

    if (roomPreviewUrl) {
      URL.revokeObjectURL(roomPreviewUrl);
    }

    setRoomFile(file);
    setRoomPreviewUrl(URL.createObjectURL(file));

    window.setTimeout(() => {
      roomPreviewSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
  };

  const toggleCategory = (category) => {
    setSelectedCategories((current) => {
      if (current.includes(category)) {
        const next = current.filter((item) => item !== category);
        return next.length ? next : current;
      }

      return [...current, category].sort(
        (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b)
      );
    });
  };

  const matchRoom = async () => {
    const validationError = validateBeforeMatch();

    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");
    setMatchesByCategory(createEmptyGroupedResults());
    setPlacedItems([]);
    setActivePlacementId(null);
    setShareMessage("");
    setShareError("");

    void recordGrowthEvent("room_upload_started", {
      page: "artist-room-matcher",
      file_type: roomFile?.type || "",
      file_size_bytes: roomFile?.size || null,
      selected_categories: [...selectedCategories],
    });

    try {
      const formData = new FormData();

      formData.append("image", roomFile);
      formData.append("email", buyerEmail.trim());
      formData.append("buyer_email", buyerEmail.trim());
      formData.append("country", country);
      formData.append("country_display", getCountryLabel(country));
      formData.append("limit", "48");
      formData.append("limit_per_category", "12");
      formData.append("category_limit", "12");
      formData.append("selected_artwork_categories", JSON.stringify(selectedCategories));
      formData.append("artwork_categories", JSON.stringify(selectedCategories));
      formData.append("categories", selectedCategories.join(","));
      formData.append("artwork_category", selectedCategories[0]);

      let response = await fetch(matchUrl, {
        method: "POST",
        body: formData,
      });

      if (response.status === 404) {
        response = await fetch(fallbackMatchUrl, {
          method: "POST",
          body: formData,
        });
      }

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            "The room could not be matched with art."
        );
      }

      const rawGrouped = extractGroupedResults(data);
      const nextGrouped = createEmptyGroupedResults();

      CATEGORY_ORDER.forEach((category) => {
        const rawItems = rawGrouped[category] || [];

        nextGrouped[category] = rawItems
          .slice(0, 12)
          .map((record, index) => normaliseArtwork(record, index, category))
          .filter((record) => record.image_url);
      });

      const nextRoomUploadId =
        data.user_upload?.id ||
        data.room_upload?.id ||
        null;

      setMatchesByCategory(nextGrouped);
      setRoomUploadId(nextRoomUploadId);

      const nextTotal = CATEGORY_ORDER.reduce(
        (sum, category) => sum + nextGrouped[category].length,
        0
      );

      void recordGrowthEvent("room_uploaded", {
        page: "artist-room-matcher",
        room_upload_id: nextRoomUploadId,
        selected_categories: [...selectedCategories],
      });

      if (nextTotal) {
        void recordGrowthEvent("match_completed", {
          page: "artist-room-matcher",
          room_upload_id: nextRoomUploadId,
          number_of_matches: nextTotal,
          selected_categories: [...selectedCategories],
          matches_by_category: CATEGORY_ORDER.reduce((summary, category) => {
            summary[category] = nextGrouped[category].length;
            return summary;
          }, {}),
        });

        setSuccess("Your room is ready. Add pieces to the preview and style it your way.");
        scrollToRoomPreview();
      } else {
        setSuccess("Your room was analysed, but no approved artworks were returned yet.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong while matching the room.");
    } finally {
      setLoading(false);
    }
  };

  const addToRoom = (artwork) => {
    if (!artwork?.id) return;

    void recordGrowthEvent("artwork_clicked", {
      page: "artist-room-matcher",
      action: "try_in_room",
      artwork_id: artwork.id,
      artwork_source: artwork.artwork_source || artwork.source_type || "",
      artwork_category: artwork.artwork_category || "",
      artwork_title: artwork.title || "",
      match_percentage: normaliseMatchScore(artwork.match_score),
    });

    const alreadyPlaced = placedItems.find((item) => item.artwork.id === artwork.id);

    if (alreadyPlaced) {
      setActivePlacementId(alreadyPlaced.placementId);
      scrollToRoomPreview();
      return;
    }

    const placement = getDefaultPlacement(artwork, stageSize, placedItems.length);
    const placementId = `${artwork.artwork_source || artwork.source_type || "art"}-${artwork.id}-${Date.now()}`;

    setPlacedItems((current) => [
      ...current,
      {
        placementId,
        artwork,
        ...placement,
      },
    ]);

    setActivePlacementId(placementId);
    scrollToRoomPreview();
  };

  const removeFromRoom = (placementId) => {
    setPlacedItems((current) => current.filter((item) => item.placementId !== placementId));

    if (activePlacementId === placementId) {
      setActivePlacementId(null);
    }
  };

  const updatePlacement = (placementId, patch) => {
    setPlacedItems((current) =>
      current.map((item) =>
        item.placementId === placementId
          ? {
              ...item,
              ...patch,
            }
          : item
      )
    );
  };

  const resizeActivePlacement = (scale) => {
    if (!activePlacementId) return;

    setPlacedItems((current) =>
      current.map((item) => {
        if (item.placementId !== activePlacementId) return item;

        const aspectRatio = getAspectRatio(item.artwork);
        const newWidth = clamp(stageSize.width * scale, 54, stageSize.width * 0.68);
        const newHeight = newWidth / aspectRatio;

        return {
          ...item,
          size: {
            width: Math.round(newWidth),
            height: Math.round(newHeight),
          },
        };
      })
    );
  };

  const resetAll = () => {
    setRoomFile(null);
    setRoomUploadId(null);
    setMatchesByCategory(createEmptyGroupedResults());
    setPlacedItems([]);
    setActivePlacementId(null);
    setError("");
    setSuccess("");
    setSharingArtworkKey("");
    setShareMessage("");
    setShareError("");
    setOfferDialogOpen(false);
    setOfferArtwork(null);
    setOfferError("");
    setOfferSentDialogOpen(false);
    setOfferSentArtwork(null);
    setOfferSentSummary("");
    setSentOfferArtworkKeys(new Set());

    if (roomPreviewUrl) {
      URL.revokeObjectURL(roomPreviewUrl);
    }

    setRoomPreviewUrl("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openOfferDialog = (artwork) => {
    if (!canSendRoomOffer(artwork)) {
      setError("This artwork is not currently open to offers.");
      return;
    }

    const artworkKey = getArtworkKey(artwork);

    if (sentOfferArtworkKeys.has(artworkKey)) {
      setOfferSentArtwork(artwork);
      setOfferSentSummary("You have already sent an offer for this artwork from this room session.");
      setOfferSentDialogOpen(true);
      return;
    }

    const minimum = getOfferMinimumValue(artwork);

    void recordGrowthEvent("offer_started", {
      page: "artist-room-matcher",
      artwork_id: artwork.id,
      artwork_source: artwork.artwork_source || artwork.source_type || "",
      artwork_category: artwork.artwork_category || "",
      artwork_title: artwork.title || "",
      minimum_offer_gbp: minimum.toFixed(2),
      room_upload_id: roomUploadId || null,
    });

    setOfferArtwork(artwork);
    setOfferForm({
      buyer_name: buyerName,
      buyer_email: buyerEmail.trim(),
      amount_gbp: minimum.toFixed(2),
      message: "I matched this artwork with my room and would like to make an offer.",
    });
    setOfferError("");
    setOfferDialogOpen(true);
  };

  const closeOfferDialog = () => {
    if (offerSubmitting) return;

    setOfferDialogOpen(false);
    setOfferArtwork(null);
    setOfferError("");
  };

  const submitRoomOffer = async () => {
    if (!offerArtwork?.id) {
      setOfferError("No artwork selected.");
      return;
    }

    const email = offerForm.buyer_email.trim();
    const amount = cleanMoneyNumber(offerForm.amount_gbp, null);
    const minimum = getOfferMinimumValue(offerArtwork);

    if (!validateEmail(email)) {
      setOfferError("Please enter a valid email.");
      return;
    }

    if (amount === null || amount < minimum) {
      setOfferError(`This artwork currently accepts offers from £${minimum.toFixed(2)}.`);
      return;
    }

    setOfferSubmitting(true);
    setOfferError("");

    try {
      const artworkSource = normaliseSource(
        offerArtwork.artwork_source || offerArtwork.source_type,
        offerArtwork.artwork_category
      );

      const payload = {
        artwork_id: offerArtwork.id,
        artwork_source: artworkSource,
        artwork_category: offerArtwork.artwork_category,
        room_upload_id: roomUploadId || null,
        buyer_name: offerForm.buyer_name.trim(),
        buyer_email: email,
        buyer_country: country,
        amount_gbp: amount.toFixed(2),
        message: offerForm.message.trim(),
      };

      if (artworkSource === "actual_painting") {
        payload.painting_id = offerArtwork.id;
      }

      const response = await fetch(offerUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        const detail =
          data.amount_gbp ||
          data.painting_id ||
          data.artwork_id ||
          data.artwork_source ||
          data.error ||
          data.message ||
          data.detail ||
          "The offer could not be sent.";

        throw new Error(Array.isArray(detail) ? detail.join(" ") : String(detail));
      }

      void recordGrowthEvent("offer_submitted", {
        page: "artist-room-matcher",
        artwork_id: offerArtwork.id,
        artwork_source: artworkSource,
        artwork_category: offerArtwork.artwork_category || "",
        artwork_title: offerArtwork.title || "",
        amount_gbp: amount.toFixed(2),
        room_upload_id: roomUploadId || null,
      });

      const artworkKey = getArtworkKey(offerArtwork);

      setBuyerEmail(email);
      setBuyerName(offerForm.buyer_name.trim());
      setSentOfferArtworkKeys((current) => {
        const next = new Set(current);
        next.add(artworkKey);
        return next;
      });

      setOfferDialogOpen(false);
      setOfferArtwork(null);

      setOfferSentArtwork(offerArtwork);
      setOfferSentSummary(
        `Your offer of £${amount.toFixed(2)} has been sent. The artist will respond by email.`
      );
      setOfferSentDialogOpen(true);
    } catch (err) {
      setOfferError(err.message || "The offer could not be sent.");
    } finally {
      setOfferSubmitting(false);
    }
  };

  const buyNow = async (artwork) => {
    if (!artwork?.id) {
      setError("No artwork selected.");
      return;
    }

    if (!validateEmail(buyerEmail.trim())) {
      setError("Please enter a valid email before buying.");
      return;
    }

    setBuyingId(artwork.id);
    setError("");
    setSuccess("");

    let checkoutTab = null;

    void recordGrowthEvent("artwork_clicked", {
      page: "artist-room-matcher",
      action: "buy_now",
      artwork_id: artwork.id,
      artwork_source: artwork.artwork_source || artwork.source_type || "",
      artwork_category: artwork.artwork_category || "",
      artwork_title: artwork.title || "",
      price_gbp: getActualPriceValue(artwork).toFixed(2),
      room_upload_id: roomUploadId || null,
    });

    try {
      checkoutTab = window.open("", "_blank", "noopener,noreferrer");

      if (checkoutTab) {
        checkoutTab.opener = null;
        checkoutTab.document.write(
          "<!doctype html><html><head><title>Opening checkout...</title></head><body style='font-family:Arial,sans-serif;padding:32px;color:#071313;background:#fbfffe'><h2>Opening secure Stripe checkout...</h2><p>Your room results will stay open in the original tab.</p></body></html>"
        );
      }

      const artworkSource = normaliseSource(
        artwork.artwork_source || artwork.source_type,
        artwork.artwork_category
      );

      const response = await fetch(buyNowUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          artwork_id: artwork.id,
          artwork_source: artworkSource,
          artwork_category: artwork.artwork_category,
          room_upload_id: roomUploadId || null,
          buyer_name: buyerName.trim(),
          buyer_email: buyerEmail.trim(),
          buyer_country: country,
          message: "Buy Now from Art For My Room room matcher.",
        }),
      });

      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            data.detail ||
            "Checkout could not be created."
        );
      }

      const checkoutUrl =
        data.checkout_url ||
        data.stripe_checkout_url ||
        data.url ||
        data.payment_url;

      if (checkoutUrl) {
        if (checkoutTab && !checkoutTab.closed) {
          checkoutTab.location.href = checkoutUrl;
        } else {
          const opened = window.open(checkoutUrl, "_blank", "noopener,noreferrer");

          if (!opened) {
            window.location.assign(checkoutUrl);
          }
        }

        setSuccess("Stripe checkout opened in a new tab. Your room results are still here.");
        return;
      }

      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.close();
      }

      setSuccess("Checkout was created, but no Stripe URL was returned.");
    } catch (err) {
      if (checkoutTab && !checkoutTab.closed) {
        checkoutTab.close();
      }

      setError(err.message || "Checkout could not be created.");
    } finally {
      setBuyingId("");
    }
  };

  const trackArtworkPageClick = (artwork, location) => {
    void recordGrowthEvent("artwork_clicked", {
      page: "artist-room-matcher",
      action: "view_artwork_page",
      click_location: location,
      artwork_id: artwork?.id || null,
      artwork_source: artwork?.artwork_source || artwork?.source_type || "",
      artwork_category: artwork?.artwork_category || "",
      artwork_title: artwork?.title || "",
      match_percentage: normaliseMatchScore(artwork?.match_score),
      room_upload_id: roomUploadId || null,
    });
  };

  const handleShareMatch = async (artwork) => {
    if (!artwork) return;

    const artworkKey = getArtworkKey(artwork) || String(artwork.id || artwork.title);

    setSharingArtworkKey(artworkKey);
    setShareMessage("");
    setShareError("");

    try {
      const sharedResult = await createGrowthShareResult(artwork);
      const shareUrl = sharedResult?.share_url;

      if (!shareUrl) {
        throw new Error("The server did not return a share link.");
      }

      const paintingTitle = artwork.title || "My matched artwork";
      const shareData = {
        title: `${paintingTitle} | ${SITE_NAME}`,
        text:
          `${paintingTitle} was matched with my room by ${SITE_NAME}. ` +
          "See the match and find art for your own room.",
        url: shareUrl,
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
          setShareMessage("Your art match is ready to travel.");
          return;
        } catch (shareFailure) {
          if (shareFailure?.name === "AbortError") {
            setShareMessage(
              "Your private share page was created. Use Share match again when you are ready."
            );
            return;
          }
        }
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setShareMessage("Share link copied to your clipboard.");
        return;
      }

      const opened = window.open(shareUrl, "_blank", "noopener,noreferrer");

      if (!opened) {
        window.prompt("Copy your Art For My Room share link:", shareUrl);
      }

      setShareMessage("Your share page has been created.");
    } catch (shareFailure) {
      console.error("Could not create the shared room result.", shareFailure);
      setShareError(
        shareFailure?.message ||
          "We could not create the share link. Please try again."
      );
    } finally {
      setSharingArtworkKey("");
    }
  };

  const renderArtworkCard = (artwork) => {
    const isPlaced = placedItems.some((item) => item.artwork.id === artwork.id);
    const category = normaliseCategory(artwork.artwork_category);
    const categoryLabel = getCategoryLabel(category);
    const artworkSeoPath = getArtworkSeoPath(artwork);
    const artworkSeoAnchorText = getArtworkSeoAnchorText(artwork);
    const palette = safeArray(artwork.colour_palette).slice(0, 5);

    return (
      <Grid item xs={12} sm={6} lg={3} key={`${artwork.artwork_source}-${artwork.id}`}>
        <Card
          elevation={0}
          sx={{
            height: "100%",
            borderRadius: 3.5,
            border: isPlaced ? `2px solid ${TURQUOISE}` : `1px solid ${LINE}`,
            bgcolor: PAPER,
            overflow: "hidden",
            transition: "0.18s ease",
            cursor: "default",
            "&:hover": {
              transform: "translateY(-3px)",
              boxShadow: "0 18px 46px rgba(17, 24, 39, 0.12)",
            },
          }}
        >
          <Box
            component={RouterLink}
            to={artworkSeoPath}
            onClick={() => trackArtworkPageClick(artwork, "result_image")}
            aria-label={`View artwork page for ${artworkSeoAnchorText}`}
            sx={{
              display: "block",
              textDecoration: "none",
              bgcolor: TURQUOISE_MIST,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardMedia
              component="img"
              image={artwork.image_url}
              alt={artworkSeoAnchorText}
              sx={{
                height: 172,
                objectFit:
                  category === "sculpture" || category === "decorative_object"
                    ? "contain"
                    : "cover",
                bgcolor: TURQUOISE_MIST,
              }}
            />

            <Chip
              label={`${artwork.match_score || 0}% match`}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                bgcolor: TURQUOISE,
                color: INK,
                fontWeight: 950,
                boxShadow: "0 5px 14px rgba(0,0,0,0.16)",
              }}
            />

            {palette.length > 0 && (
              <Stack
                direction="row"
                spacing={0.4}
                sx={{
                  position: "absolute",
                  left: 10,
                  bottom: 10,
                  p: 0.5,
                  borderRadius: 999,
                  bgcolor: alpha(WHITE, 0.84),
                  backdropFilter: "blur(8px)",
                }}
              >
                {palette.map((item, index) => {
                  const color = typeof item === "string" ? item : item.hex || item.color || item.value;

                  return (
                    <Box
                      key={`${color}-${index}`}
                      sx={{
                        width: 13,
                        height: 13,
                        borderRadius: "50%",
                        bgcolor: color || TURQUOISE,
                        border: `1px solid ${alpha(INK, 0.12)}`,
                      }}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          <CardContent sx={{ p: 1.8 }}>
            <Stack spacing={1.15}>
              <Stack direction="row" spacing={1} justifyContent="space-between">
                <Chip
                  label={categoryLabel}
                  size="small"
                  sx={{
                    bgcolor: category === "painting" || category === "sculpture" ? BURGUNDY_MIST : TURQUOISE_MIST,
                    color: category === "painting" || category === "sculpture" ? BURGUNDY : TURQUOISE_DARK,
                    fontWeight: 900,
                  }}
                />

                <Typography sx={{ fontWeight: 950, color: INK }}>
                  {getPriceLabel(artwork)}
                </Typography>
              </Stack>

              <Box>
                <Typography
                  sx={{
                    fontWeight: 950,
                    lineHeight: 1.18,
                    minHeight: 38,
                    color: INK,
                  }}
                >
                  {artwork.title}
                </Typography>

                <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.45 }}>
                  {artwork.buyer_facing_artist_name || artwork.artist_name || "Artist"} · {artwork.size_label || "Size TBC"}
                </Typography>
              </Box>

              <Divider />

              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <SmallMeta label="Medium" value={getArtworkMediumDisplay(artwork)} />
                </Grid>
                <Grid item xs={6}>
                  <SmallMeta label="Country" value={getArtworkCountryDisplay(artwork)} />
                </Grid>
              </Grid>

              <Typography sx={{ color: MUTED, fontSize: 12.5, lineHeight: 1.45 }}>
                {getCommerceNote(artwork)}
              </Typography>

              <Stack direction="row" spacing={1}>
                <SecondaryButton
                  fullWidth
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addToRoom(artwork)}
                >
                  {isPlaced ? "Placed" : "Try"}
                </SecondaryButton>

                {canBuyNow(artwork) && (
                  <PrimaryButton
                    fullWidth
                    size="small"
                    startIcon={
                      buyingId === artwork.id ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PaymentIcon />
                      )
                    }
                    disabled={buyingId === artwork.id}
                    onClick={() => buyNow(artwork)}
                  >
                    Buy
                  </PrimaryButton>
                )}
              </Stack>

              {canSendRoomOffer(artwork) && (
                <BurgundyButton
                  fullWidth
                  size="small"
                  startIcon={<LocalOfferIcon />}
                  onClick={() => openOfferDialog(artwork)}
                >
                  Make offer
                </BurgundyButton>
              )}

              <SecondaryButton
                fullWidth
                size="small"
                startIcon={
                  sharingArtworkKey === getArtworkKey(artwork) ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <ShareIcon />
                  )
                }
                disabled={sharingArtworkKey === getArtworkKey(artwork)}
                onClick={() => handleShareMatch(artwork)}
              >
                {sharingArtworkKey === getArtworkKey(artwork)
                  ? "Creating link..."
                  : "Share match"}
              </SecondaryButton>

              <GhostButton
                component={RouterLink}
                to={artworkSeoPath}
                onClick={() => trackArtworkPageClick(artwork, "result_button")}
                fullWidth
                size="small"
                startIcon={<ImageSearchIcon />}
              >
                View artwork page
              </GhostButton>
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const renderCategorySection = (categoryObject) => {
    const category = categoryObject.value;
    const items = matchesByCategory[category] || [];
    const isSelected = selectedCategories.includes(category);

    if (!isSelected) return null;

    return (
      <Box key={category}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2.5,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: categoryObject.negotiable ? BURGUNDY_MIST : TURQUOISE_MIST,
                  color: categoryObject.negotiable ? BURGUNDY : TURQUOISE_DARK,
                }}
              >
                {categoryObject.icon}
              </Box>

              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: { xs: "1.35rem", md: "1.6rem" },
                  letterSpacing: "-0.045em",
                }}
              >
                {categoryObject.resultTitle}
              </Typography>
            </Stack>

            <Typography sx={{ color: MUTED }}>
              {categoryObject.resultSubtitle}
            </Typography>
          </Box>

          <Chip
            label={`${items.length}/12`}
            sx={{
              bgcolor: categoryObject.negotiable ? BURGUNDY_MIST : TURQUOISE_MIST,
              color: categoryObject.negotiable ? BURGUNDY : TURQUOISE_DARK,
              fontWeight: 950,
            }}
          />
        </Stack>

        {items.length === 0 ? (
          <Box
            sx={{
              p: 2.2,
              borderRadius: 3,
              border: `1px solid ${LINE}`,
              bgcolor: "#fbfffe",
            }}
          >
            <Typography sx={{ color: MUTED }}>
              {categoryObject.emptyText}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {items.slice(0, 12).map(renderArtworkCard)}
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <PageShell>
      <AppHeader elevation={0}>
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 2 }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ py: { xs: 1.8, md: 1.3 } }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                component="img"
                src={ASSETS.logo}
                alt="Art For My Room logo"
                sx={{
                  width: 42,
                  height: 42,
                  objectFit: "contain",
                  bgcolor: WHITE,
                  borderRadius: 2,
                  p: 0.45,
                }}
              />

              <Box>
                <Typography sx={{ fontWeight: 950, lineHeight: 1 }}>
                  Art<span style={{ color: TURQUOISE }}>For</span>MyRoom
                </Typography>
                <Typography sx={{ color: alpha(WHITE, 0.62), fontSize: 12 }}>
                  Real rooms · real artists · real choices
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                label="Find art"
                sx={{
                  bgcolor: TURQUOISE,
                  color: INK,
                  fontWeight: 950,
                }}
              />
              <Chip
                label="Buy now"
                sx={{
                  bgcolor: alpha(WHITE, 0.08),
                  color: alpha(WHITE, 0.78),
                  border: `1px solid ${alpha(WHITE, 0.10)}`,
                  fontWeight: 850,
                }}
              />
              <Chip
                label="Make offer"
                sx={{
                  bgcolor: alpha(BURGUNDY, 0.55),
                  color: WHITE,
                  border: `1px solid ${alpha(WHITE, 0.10)}`,
                  fontWeight: 850,
                }}
              />
            </Stack>
          </Stack>
        </Container>
      </AppHeader>

      <Box component="main">
        <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 4 } }}>
          <Stack spacing={3}>
            <Box
              sx={{
                borderRadius: { xs: 4, md: 5 },
                overflow: "hidden",
                bgcolor: INK,
                color: WHITE,
                boxShadow: "0 30px 90px rgba(17,24,39,0.24)",
              }}
            >
              <Grid container>
                <Grid item xs={12} md={5.5}>
                  <Box sx={{ p: { xs: 3, md: 5 }, minHeight: { md: 540 }, position: "relative" }}>
                    <PaintStreak sx={{ top: "28%", opacity: 0.9 }} />

                    <Stack spacing={2.2} sx={{ position: "relative", zIndex: 1 }}>
                      <Chip
                        icon={<AutoAwesomeIcon />}
                        label="AI room decor matcher"
                        sx={{
                          alignSelf: "flex-start",
                          bgcolor: alpha(WHITE, 0.08),
                          color: WHITE,
                          border: `1px solid ${alpha(WHITE, 0.12)}`,
                          fontWeight: 900,
                          "& .MuiChip-icon": { color: TURQUOISE },
                        }}
                      />

                      <Typography
                        component="h1"
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontSize: { xs: "2.55rem", sm: "3.7rem", md: "5.1rem" },
                          fontWeight: 950,
                          letterSpacing: "-0.085em",
                          lineHeight: 0.9,
                          maxWidth: 760,
                        }}
                      >
                        Your room tells us what art belongs in it.
                      </Typography>

                      <Typography
                        sx={{
                          color: alpha(WHITE, 0.74),
                          fontSize: { xs: "1rem", md: "1.08rem" },
                          lineHeight: 1.75,
                          maxWidth: 680,
                        }}
                      >
                        Upload a room photo. We match it with paintings, prints, sculptures
                        and decorative objects, then you can place each piece inside your room
                        before buying or making an offer.
                      </Typography>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                        <PrimaryButton
                          startIcon={<CloudUploadIcon />}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Upload room
                        </PrimaryButton>

                        <SecondaryButton
                          component="a"
                          href="#room-studio"
                          startIcon={<ArrowForwardIcon />}
                          sx={{
                            bgcolor: alpha(WHITE, 0.08),
                            color: WHITE,
                            borderColor: alpha(WHITE, 0.20),
                            "&:hover": {
                              bgcolor: alpha(WHITE, 0.15),
                              borderColor: alpha(WHITE, 0.34),
                            },
                          }}
                        >
                          Open studio
                        </SecondaryButton>
                      </Stack>

                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ pt: 1 }}>
                        <StatBox value="4" label="art categories" />
                        <StatBox value="12" label="per category" />
                        <StatBox value="3" label="offer chances" />
                      </Stack>
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6.5}>
                  <Box sx={{ p: { xs: 2, md: 2.5 }, height: "100%" }}>
                    <Box
                      sx={{
                        height: "100%",
                        minHeight: { xs: 330, md: 540 },
                        borderRadius: 4,
                        overflow: "hidden",
                        border: `1px solid ${alpha(WHITE, 0.12)}`,
                        bgcolor: NAVY,
                        position: "relative",
                      }}
                    >
                      <Box
                        component="video"
                        src={ASSETS.findVideo}
                        controls
                        muted
                        playsInline
                        preload="metadata"
                        sx={{
                          width: "100%",
                          height: "100%",
                          minHeight: { xs: 330, md: 540 },
                          display: "block",
                          objectFit: "cover",
                          bgcolor: NAVY,
                        }}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          left: 16,
                          bottom: 16,
                          right: 16,
                          p: 1.6,
                          borderRadius: 3,
                          bgcolor: alpha(INK, 0.62),
                          border: `1px solid ${alpha(WHITE, 0.13)}`,
                          backdropFilter: "blur(18px)",
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PlayCircleOutlineIcon sx={{ color: TURQUOISE }} />
                          <Box>
                            <Typography sx={{ fontWeight: 950 }}>
                              Preview the room before you commit
                            </Typography>
                            <Typography sx={{ color: alpha(WHITE, 0.68), fontSize: 13 }}>
                              Drag, resize, compare and choose with confidence
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Grid container spacing={3} id="room-studio">
              <Grid item xs={12} lg={4}>
                <AppPanel elevation={0} sx={{ p: { xs: 2.4, md: 3 }, height: "100%" }}>
                  <Stack spacing={2.2}>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 950,
                          fontSize: "1.8rem",
                          letterSpacing: "-0.06em",
                        }}
                      >
                        Start the match
                      </Typography>
                      <Typography sx={{ color: MUTED, lineHeight: 1.65 }}>
                        Add your room, choose what to match, then let the studio fetch the art.
                      </Typography>
                    </Box>

                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: 13 }}>
                          Setup progress
                        </Typography>
                        <Typography sx={{ color: MUTED, fontWeight: 850, fontSize: 13 }}>
                          {setupProgress}%
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={setupProgress}
                        sx={{
                          height: 7,
                          borderRadius: 999,
                          bgcolor: alpha(TURQUOISE, 0.14),
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            background: `linear-gradient(90deg, ${TURQUOISE}, ${GOLD})`,
                          },
                        }}
                      />
                    </Box>

                    <MinimalInput
                      fullWidth
                      label="Your name, optional"
                      value={buyerName}
                      onChange={(event) => setBuyerName(event.target.value)}
                    />

                    <MinimalInput
                      fullWidth
                      required
                      label="Your email"
                      type="email"
                      value={buyerEmail}
                      onChange={(event) => setBuyerEmail(event.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: TURQUOISE_DARK }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <MinimalInput
                      fullWidth
                      required
                      select
                      label="Country"
                      value={country}
                      onChange={(event) => setCountry(event.target.value)}
                    >
                      {COUNTRY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MinimalInput>

                    <Box>
                      <Typography sx={{ fontWeight: 950, mb: 1 }}>
                        Match these categories
                      </Typography>

                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {ARTWORK_CATEGORIES.map((category) => (
                          <CategoryChip
                            key={category.value}
                            category={category}
                            selected={selectedCategories.includes(category.value)}
                            onClick={() => toggleCategory(category.value)}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={handleRoomFileChange}
                    />

                    <UploadZone onClick={() => fileInputRef.current?.click()}>
                      {roomPreviewUrl ? (
                        <Stack spacing={1.2} alignItems="center">
                          <Box
                            component="img"
                            src={roomPreviewUrl}
                            alt="Uploaded room preview"
                            sx={{
                              width: "100%",
                              maxHeight: 150,
                              objectFit: "cover",
                              borderRadius: 2,
                              border: `1px solid ${LINE}`,
                            }}
                          />
                          <Typography sx={{ color: TURQUOISE_DARK, fontWeight: 900 }}>
                            Room photo loaded
                          </Typography>
                          <Typography sx={{ color: MUTED, fontSize: 13 }}>
                            Click to change image
                          </Typography>
                        </Stack>
                      ) : (
                        <Stack spacing={1} alignItems="center">
                          <CloudUploadIcon sx={{ fontSize: 42, color: TURQUOISE_DARK }} />
                          <Typography sx={{ fontWeight: 950 }}>
                            Drop or upload room image
                          </Typography>
                          <Typography sx={{ color: MUTED, fontSize: 14 }}>
                            JPG, PNG or WEBP
                          </Typography>
                        </Stack>
                      )}

                      {roomFile && (
                        <Chip
                          label={roomFile.name}
                          sx={{
                            mt: 1.5,
                            bgcolor: GOLD_MIST,
                            color: GOLD_DARK,
                            fontWeight: 850,
                            maxWidth: "100%",
                          }}
                        />
                      )}
                    </UploadZone>

                    {error && (
                      <Alert severity="error" onClose={() => setError("")}>
                        {error}
                      </Alert>
                    )}

                    {success && (
                      <Alert severity="success" onClose={() => setSuccess("")}>
                        {success}
                      </Alert>
                    )}

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <PrimaryButton
                        fullWidth
                        startIcon={
                          loading ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <ImageSearchIcon />
                          )
                        }
                        disabled={loading}
                        onClick={matchRoom}
                      >
                        {loading ? "Matching..." : "Find art"}
                      </PrimaryButton>

                      <SecondaryButton startIcon={<RestartAltIcon />} onClick={resetAll}>
                        Reset
                      </SecondaryButton>
                    </Stack>
                  </Stack>
                </AppPanel>
              </Grid>

              <Grid item xs={12} lg={8}>
                <CanvasPanel
                  ref={roomPreviewSectionRef}
                  elevation={0}
                  sx={{ p: { xs: 2, md: 2.4 }, height: "100%" }}
                >
                  <Stack spacing={1.8}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      alignItems={{ xs: "flex-start", md: "center" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.35 }}>
                          <Typography
                            sx={{
                              fontFamily: HEADING_FONT,
                              fontWeight: 950,
                              fontSize: "1.8rem",
                              letterSpacing: "-0.06em",
                            }}
                          >
                            Room studio
                          </Typography>
                          <Chip
                            size="small"
                            label="drag & resize"
                            sx={{ bgcolor: TURQUOISE_MIST, color: TURQUOISE_DARK, fontWeight: 900 }}
                          />
                        </Stack>

                        <Typography sx={{ color: MUTED }}>
                          Add matched pieces below. Move them until the room starts whispering yes.
                        </Typography>
                      </Box>

                      <Chip
                        icon={<HomeRoundedIcon />}
                        label={`${placedItems.length} item${placedItems.length === 1 ? "" : "s"} placed`}
                        sx={{
                          bgcolor: BURGUNDY_MIST,
                          color: BURGUNDY,
                          fontWeight: 950,
                        }}
                      />
                    </Stack>

                    <Box
                      ref={stageRef}
                      sx={{
                        position: "relative",
                        width: "100%",
                        minHeight: { xs: 380, md: 570 },
                        borderRadius: 3,
                        overflow: "hidden",
                        border: `1px solid ${LINE}`,
                        bgcolor: TURQUOISE_MIST,
                        userSelect: "none",
                        touchAction: "none",
                      }}
                    >
                      {roomPreviewUrl ? (
                        <Box
                          component="img"
                          src={roomPreviewUrl}
                          alt="Uploaded room"
                          draggable={false}
                          sx={{
                            width: "100%",
                            height: "100%",
                            minHeight: { xs: 380, md: 570 },
                            display: "block",
                            objectFit: "cover",
                            userSelect: "none",
                            pointerEvents: "none",
                          }}
                        />
                      ) : (
                        <Stack
                          alignItems="center"
                          justifyContent="center"
                          spacing={1}
                          sx={{
                            minHeight: { xs: 380, md: 570 },
                            textAlign: "center",
                            p: 3,
                          }}
                        >
                          <WallpaperIcon sx={{ fontSize: 64, color: TURQUOISE_DARK }} />
                          <Typography sx={{ fontWeight: 950 }}>
                            Your room preview appears here
                          </Typography>
                          <Typography sx={{ color: MUTED, maxWidth: 420 }}>
                            Upload a room image and start decorating with matched art.
                          </Typography>
                        </Stack>
                      )}

                      {roomPreviewUrl &&
                        placedItems.map((item) => {
                          const isActive = item.placementId === activePlacementId;
                          const category = normaliseCategory(item.artwork.artwork_category);
                          const artworkImage =
                            item.artwork.transparent_cutout_image_url ||
                            item.artwork.image_url;

                          return (
                            <Rnd
                              key={item.placementId}
                              size={item.size}
                              position={item.position}
                              bounds="parent"
                              lockAspectRatio={getAspectRatio(item.artwork)}
                              minWidth={50}
                              minHeight={50}
                              dragGrid={[1, 1]}
                              resizeGrid={[1, 1]}
                              onDragStart={() => setActivePlacementId(item.placementId)}
                              onDragStop={(event, data) => {
                                updatePlacement(item.placementId, {
                                  position: {
                                    x: data.x,
                                    y: data.y,
                                  },
                                });
                              }}
                              onResizeStart={() => setActivePlacementId(item.placementId)}
                              onResizeStop={(event, direction, ref, delta, position) => {
                                updatePlacement(item.placementId, {
                                  size: {
                                    width: parseInt(ref.style.width, 10),
                                    height: parseInt(ref.style.height, 10),
                                  },
                                  position,
                                });
                              }}
                              style={{
                                zIndex: isActive ? 20 : 10,
                                border: isActive
                                  ? `2px solid ${GOLD}`
                                  : `1px solid ${alpha(WHITE, 0.1)}`,
                                boxShadow: isActive
                                  ? `0 14px 34px ${alpha(BURGUNDY, 0.34)}`
                                  : "0 12px 28px rgba(0,0,0,0.20)",
                                borderRadius:
                                  category === "painting" || category === "print"
                                    ? 2
                                    : 12,
                                overflow: "hidden",
                                background: "transparent",
                                cursor: "move",
                              }}
                              resizeHandleStyles={{
                                bottomRight: resizeHandleStyle(),
                                bottomLeft: resizeHandleStyle(),
                                topRight: resizeHandleStyle(),
                                topLeft: resizeHandleStyle(),
                              }}
                            >
                              <Box
                                onClick={() => setActivePlacementId(item.placementId)}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  position: "relative",
                                  bgcolor:
                                    category === "painting" || category === "print"
                                      ? WHITE
                                      : "transparent",
                                  border:
                                    category === "painting" || category === "print"
                                      ? `8px solid ${WHITE}`
                                      : "none",
                                }}
                              >
                                <Box
                                  component="img"
                                  src={artworkImage}
                                  alt={item.artwork.title}
                                  draggable={false}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: "block",
                                    pointerEvents: "none",
                                    userSelect: "none",
                                  }}
                                />
                              </Box>
                            </Rnd>
                          );
                        })}
                    </Box>

                    {activePlacedItem && (
                      <Box
                        sx={{
                          borderRadius: 3,
                          p: 1.6,
                          bgcolor: INK,
                          color: WHITE,
                          border: `1px solid ${alpha(WHITE, 0.12)}`,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.4}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", md: "center" }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 950 }}>
                              {activePlacedItem.artwork.title}
                            </Typography>
                            <Typography sx={{ color: alpha(WHITE, 0.64), fontSize: 13 }}>
                              {getCategoryLabel(activePlacedItem.artwork.artwork_category)} · {getPriceLabel(activePlacedItem.artwork)}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <SecondaryButton
                              size="small"
                              startIcon={<StraightenIcon />}
                              onClick={() => resizeActivePlacement(0.16)}
                              sx={{
                                bgcolor: alpha(WHITE, 0.08),
                                color: WHITE,
                                borderColor: alpha(WHITE, 0.18),
                                "&:hover": { bgcolor: alpha(WHITE, 0.15) },
                              }}
                            >
                              Small
                            </SecondaryButton>
                            <SecondaryButton
                              size="small"
                              startIcon={<StraightenIcon />}
                              onClick={() => resizeActivePlacement(0.24)}
                              sx={{
                                bgcolor: alpha(WHITE, 0.08),
                                color: WHITE,
                                borderColor: alpha(WHITE, 0.18),
                                "&:hover": { bgcolor: alpha(WHITE, 0.15) },
                              }}
                            >
                              Medium
                            </SecondaryButton>
                            <SecondaryButton
                              size="small"
                              startIcon={<StraightenIcon />}
                              onClick={() => resizeActivePlacement(0.34)}
                              sx={{
                                bgcolor: alpha(WHITE, 0.08),
                                color: WHITE,
                                borderColor: alpha(WHITE, 0.18),
                                "&:hover": { bgcolor: alpha(WHITE, 0.15) },
                              }}
                            >
                              Large
                            </SecondaryButton>
                            <IconButton
                              aria-label="Remove selected artwork from room"
                              onClick={() => removeFromRoom(activePlacedItem.placementId)}
                              sx={{
                                color: WHITE,
                                bgcolor: alpha(BURGUNDY, 0.72),
                                "&:hover": { bgcolor: BURGUNDY },
                              }}
                            >
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </CanvasPanel>
              </Grid>
            </Grid>

            <AppPanel elevation={0} sx={{ p: { xs: 2.4, md: 3 } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                sx={{ mb: 2.5 }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: HEADING_FONT,
                      fontWeight: 950,
                      fontSize: "1.85rem",
                      letterSpacing: "-0.06em",
                    }}
                  >
                    Matched art
                  </Typography>
                  <Typography sx={{ color: MUTED }}>
                    Results are separated by category. Try a few pieces and build the room.
                  </Typography>
                </Box>

                <Chip
                  icon={<TuneIcon />}
                  label={`${totalMatches} match${totalMatches === 1 ? "" : "es"}`}
                  sx={{
                    bgcolor: TURQUOISE_MIST,
                    color: TURQUOISE_DARK,
                    fontWeight: 950,
                  }}
                />
              </Stack>

              {shareMessage && (
                <Alert
                  severity="info"
                  onClose={() => setShareMessage("")}
                  sx={{ mb: 2.5 }}
                >
                  {shareMessage}
                </Alert>
              )}

              {shareError && (
                <Alert
                  severity="warning"
                  onClose={() => setShareError("")}
                  sx={{ mb: 2.5 }}
                >
                  {shareError}
                </Alert>
              )}

              {loading && (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <CircularProgress sx={{ color: TURQUOISE_DARK, mb: 2 }} />
                  <Typography sx={{ fontWeight: 950 }}>
                    Matching your room with four art categories...
                  </Typography>
                  <Typography sx={{ color: MUTED, mt: 0.6 }}>
                    Reading colour, mood and scale, then finding suitable artworks.
                  </Typography>
                </Box>
              )}

              {!loading && totalMatches === 0 && (
                <Box
                  sx={{
                    borderRadius: 3,
                    p: 4,
                    textAlign: "center",
                    bgcolor: "#fbfffe",
                    border: `1px solid ${LINE}`,
                  }}
                >
                  <PaletteIcon sx={{ fontSize: 52, color: TURQUOISE_DARK, mb: 1 }} />
                  <Typography sx={{ fontWeight: 950, fontSize: "1.2rem" }}>
                    No matches yet
                  </Typography>
                  <Typography sx={{ color: MUTED }}>
                    Upload a room and choose at least one art category.
                  </Typography>
                </Box>
              )}

              {!loading && totalMatches > 0 && (
                <Stack spacing={4}>
                  {selectedCategoryObjects.map(renderCategorySection)}
                </Stack>
              )}
            </AppPanel>
          </Stack>
        </Container>

        <Box
          component="footer"
          sx={{
            mt: 5,
            py: 5,
            bgcolor: INK,
            color: WHITE,
            borderTop: `1px solid ${alpha(WHITE, 0.08)}`,
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={2.2} alignItems="center">
              <Box
                component="img"
                src={ASSETS.logo}
                alt="Art For My Room logo"
                sx={{
                  width: 70,
                  height: 70,
                  objectFit: "contain",
                  bgcolor: WHITE,
                  borderRadius: 3,
                  p: 0.6,
                }}
              />

              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: { xs: "1.8rem", md: "2.6rem" },
                  letterSpacing: "-0.07em",
                  textAlign: "center",
                }}
              >
                Make the room feel chosen.
              </Typography>

              <Typography
                sx={{
                  color: alpha(WHITE, 0.70),
                  textAlign: "center",
                  maxWidth: 720,
                  lineHeight: 1.75,
                }}
              >
                Upload your room and decorate it with matched paintings, prints,
                sculptures and decorative objects.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} justifyContent="center">
                <PrimaryButton component="button" onClick={() => fileInputRef.current?.click()}>
                  Upload Room
                </PrimaryButton>

                <SecondaryButton
                  component={RouterLink}
                  to="/artist-launch"
                  sx={{
                    bgcolor: alpha(WHITE, 0.08),
                    color: WHITE,
                    borderColor: alpha(WHITE, 0.24),
                    "&:hover": {
                      bgcolor: alpha(WHITE, 0.16),
                    },
                  }}
                >
                  Upload Artist Work
                </SecondaryButton>

                <SecondaryButton
                  component="a"
                  href={`mailto:${CONTACT_EMAIL}`}
                  sx={{
                    bgcolor: alpha(WHITE, 0.08),
                    color: WHITE,
                    borderColor: alpha(WHITE, 0.24),
                    "&:hover": {
                      bgcolor: alpha(WHITE, 0.16),
                    },
                  }}
                >
                  Email Us
                </SecondaryButton>
              </Stack>

              <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", width: "100%", pt: 2 }} />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="center">
                <Link
                  href={`mailto:${CONTACT_EMAIL}`}
                  sx={{
                    color: "rgba(255,255,255,0.80)",
                    textDecoration: "none",
                    fontWeight: 850,
                  }}
                >
                  {CONTACT_EMAIL}
                </Link>

                <Link
                  component={RouterLink}
                  to="/commitments/privacy-policy"
                  sx={{
                    color: "rgba(255,255,255,0.70)",
                    textDecoration: "none",
                    fontWeight: 800,
                  }}
                >
                  Privacy Policy
                </Link>
              </Stack>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.42)",
                  fontSize: "0.88rem",
                  fontFamily: MONO_FONT,
                  pt: 1,
                  textAlign: "center",
                }}
              >
                © {new Date().getFullYear()} {SITE_NAME} · turquoise, gold and burgundy
              </Typography>
            </Stack>
          </Container>
        </Box>
      </Box>

      <Dialog
        open={offerSentDialogOpen}
        onClose={() => setOfferSentDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack spacing={2}>
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: TURQUOISE_MIST,
                color: TURQUOISE_DARK,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 34 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: HEADING_FONT,
                  fontWeight: 950,
                  fontSize: "1.55rem",
                  letterSpacing: "-0.045em",
                }}
              >
                Offer sent
              </Typography>

              <Typography sx={{ color: MUTED, lineHeight: 1.7, mt: 0.8 }}>
                {offerSentSummary || "Your offer has been sent to the artist."}
              </Typography>
            </Box>

            {offerSentArtwork && (
              <Box
                sx={{
                  borderRadius: 2.5,
                  p: 1.4,
                  bgcolor: CANVAS,
                  border: `1px solid ${LINE}`,
                }}
              >
                <Typography sx={{ fontWeight: 950 }}>
                  {offerSentArtwork.title}
                </Typography>
                <Typography sx={{ color: MUTED, fontSize: 13 }}>
                  {getPriceLabel(offerSentArtwork)} · {getCategoryLabel(offerSentArtwork.artwork_category)}
                </Typography>
              </Box>
            )}

            <PrimaryButton
              fullWidth
              onClick={() => setOfferSentDialogOpen(false)}
            >
              Continue styling room
            </PrimaryButton>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={offerDialogOpen}
        onClose={closeOfferDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {offerArtwork && (
            <Box>
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  color: WHITE,
                  bgcolor: INK,
                  position: "relative",
                }}
              >
                <IconButton
                  onClick={closeOfferDialog}
                  disabled={offerSubmitting}
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    color: WHITE,
                    bgcolor: alpha(WHITE, 0.08),
                    "&:hover": { bgcolor: alpha(WHITE, 0.16) },
                  }}
                >
                  <CloseIcon />
                </IconButton>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    component="img"
                    src={offerArtwork.image_url}
                    alt={offerArtwork.title}
                    sx={{
                      width: 74,
                      height: 74,
                      objectFit: "cover",
                      borderRadius: 2,
                      border: `1px solid ${alpha(WHITE, 0.18)}`,
                      bgcolor: WHITE,
                    }}
                  />

                  <Box sx={{ minWidth: 0, pr: 4 }}>
                    <Chip
                      label="Room offer"
                      size="small"
                      sx={{
                        bgcolor: TURQUOISE,
                        color: INK,
                        fontWeight: 950,
                        mb: 0.8,
                      }}
                    />

                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 950,
                        fontSize: "1.45rem",
                        letterSpacing: "-0.045em",
                        lineHeight: 1.05,
                      }}
                    >
                      {offerArtwork.title}
                    </Typography>

                    <Typography sx={{ color: alpha(WHITE, 0.66), fontSize: 13, mt: 0.4 }}>
                      {offerArtwork.buyer_facing_artist_name || offerArtwork.artist_name || "Artist"}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack spacing={2} sx={{ p: { xs: 2.5, md: 3 } }}>
                <Box
                  sx={{
                    borderRadius: 2.5,
                    p: 1.6,
                    bgcolor: CANVAS,
                    border: `1px solid ${LINE}`,
                  }}
                >
                  <Grid container spacing={1.4}>
                    <Grid item xs={6}>
                      <SmallMeta label="Listed price" value={getPriceLabel(offerArtwork)} />
                    </Grid>
                    <Grid item xs={6}>
                      <SmallMeta label="Minimum offer" value={getOfferFloorLabel(offerArtwork)} />
                    </Grid>
                    <Grid item xs={6}>
                      <SmallMeta
                        label="Delivery"
                        value={offerArtwork.estimated_postage_fee_label || "Estimate TBC"}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <SmallMeta label="Chances" value="3 offers" />
                    </Grid>
                  </Grid>
                </Box>

                <MinimalInput
                  fullWidth
                  label="Your name, optional"
                  value={offerForm.buyer_name}
                  onChange={(event) =>
                    setOfferForm((current) => ({
                      ...current,
                      buyer_name: event.target.value,
                    }))
                  }
                />

                <MinimalInput
                  fullWidth
                  label="Your email"
                  type="email"
                  value={offerForm.buyer_email}
                  onChange={(event) =>
                    setOfferForm((current) => ({
                      ...current,
                      buyer_email: event.target.value,
                    }))
                  }
                />

                <MinimalInput
                  fullWidth
                  label="Your offer"
                  value={offerForm.amount_gbp}
                  onChange={(event) =>
                    setOfferForm((current) => ({
                      ...current,
                      amount_gbp: event.target.value,
                    }))
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                />

                <MinimalInput
                  fullWidth
                  multiline
                  minRows={3}
                  label="Message, optional"
                  value={offerForm.message}
                  onChange={(event) =>
                    setOfferForm((current) => ({
                      ...current,
                      message: event.target.value,
                    }))
                  }
                />

                {offerError && (
                  <Alert severity="error" onClose={() => setOfferError("")}>
                    {offerError}
                  </Alert>
                )}

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <BurgundyButton
                    fullWidth
                    startIcon={
                      offerSubmitting ? (
                        <CircularProgress size={17} color="inherit" />
                      ) : (
                        <LocalOfferIcon />
                      )
                    }
                    disabled={offerSubmitting}
                    onClick={submitRoomOffer}
                  >
                    {offerSubmitting ? "Sending..." : "Send offer"}
                  </BurgundyButton>

                  <SecondaryButton fullWidth onClick={closeOfferDialog}>
                    Close
                  </SecondaryButton>
                </Stack>

                <Typography sx={{ color: MUTED, fontSize: 12, lineHeight: 1.6 }}>
                  Room Offers are only for original paintings and sculptures. The artist can
                  accept, counter or decline. Each side has 3 chances.
                </Typography>
              </Stack>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}

export default ActualRoomMatcherPage;
